import type { PersonalBookingData, CheckBookableResponse } from 'bookwithme-attendee';
import {
    checkBookableAndUpdateTotalBookings,
    getBookingSessionData,
    lazySetBookingData,
    lazySetBookingMailbox,
    lazySetBookingSignedUrl,
    lazySetInitialRequestState,
    lazySetDisplayTimeZone,
    lazySetEditBooking,
    lazyInitiateCookieRefresh,
    lazyTrySetBookingUser,
    lazySetBookingPageErrorState,
    lazySetSignedUrlBookabilityData,
    initBookWithMeFreeBusyCache,
    lazySetAttendeeCurrentMeetingTypeID,
} from 'bookwithme-attendee';
import type RequestState from 'bookwithme-shared/lib/store/schema/RequestState';
import { BookingPageErrorState } from 'bookwithme-shared/lib/store/schema/BookingPageErrorState';
import type BookWithMeUrlData from 'bookwithme-main-module/lib/schema/BookWithMeUrlData';
import { isAnonymousUser } from 'bookings-shared/lib/c2/utils/isAnonymousUser';
import getUserWorkingHoursTzId from 'owa-session-store/lib/selectors/getUserWorkingHoursTzId';
import { getOSTimeZoneId } from 'owa-datetime-store';
import { setLocale } from 'owa-localize';
import { getSupportedFallbackForLocale } from 'owa-shared-bootstrap/lib/getSupportedFallbackForLocale';
import jwt_decode from 'jwt-decode';
import { now } from 'owa-datetime';
import type { BookingService } from 'bookwithme-attendee/lib/store/schema/BookingService';
import { parseMeetingTypeDescription } from 'bookwithme-organizer/lib/utils/parseServiceDescription';
import { lazyRetrievePbEnabledStatus } from 'bookwithme-organizer';
import type { SchedulingPolicy } from 'bookwithme-attendee/lib/store/schema/SchedulingPolicy';
import { logStartUsage } from 'owa-analytics-start';

export function runAfterBootstrap(props: BookWithMeUrlData) {
    const { getRequestUrl, bookingMailbox, meetingTypeId, bookingId, entryPoint } = props;
    lazySetInitialRequestState.importAndExecute(1);
    lazySetBookingMailbox.importAndExecute(bookingMailbox);
    if (meetingTypeId) {
        lazySetAttendeeCurrentMeetingTypeID.importAndExecute(meetingTypeId);
    }
    initBookWithMeFreeBusyCache();

    if (getRequestUrl) {
        lazySetBookingSignedUrl.importAndExecute(getRequestUrl);
        const requestUrlParams = new URLSearchParams(getRequestUrl);
        /* eslint-disable-next-line @typescript-eslint/no-shadow  -- (https://aka.ms/OWALintWiki)
         * See https://typescript-eslint.io/rules/no-shadow
         *	> 'meetingTypeId' is already declared in the upper scope. */
        const meetingTypeId = requestUrlParams.get('itemid') ?? '';
        lazySetAttendeeCurrentMeetingTypeID.importAndExecute(meetingTypeId);
        if (!meetingTypeId) {
            lazySetBookingPageErrorState.importAndExecute(BookingPageErrorState.InvalidSignedUrl);
            lazySetInitialRequestState.importAndExecute(3);
            return;
        }
        checkBookableAndUpdateTotalBookings(false /*shouldBook*/, getRequestUrl).then(
            bookableResponse => {
                // If bookingId is present, we'll show an existing booking data without checking for isBookable.
                if (bookableResponse?.isBookable || bookingId) {
                    lazySetSignedUrlBookabilityData.importAndExecute(
                        !!bookableResponse?.bookingUrlType?.isSingleUse,
                        !!bookableResponse?.bookingUrlType?.isAttendeeAuthRequired,
                        bookableResponse?.recipients ?? [],
                        bookableResponse?.attendeeHasAccessThroughGroupOrDL ?? false,
                        bookableResponse?.preBuffer ?? 0,
                        bookableResponse?.postBuffer ?? 0
                    );
                    getBookingPageSessionData(
                        bookingMailbox,
                        meetingTypeId,
                        bookingId,
                        entryPoint,
                        bookableResponse
                    );
                } else {
                    lazySetInitialRequestState.importAndExecute(3);
                    const authToken = requestUrlParams.get('authtoken');
                    if (authToken && isAuthTokenExpired(authToken)) {
                        lazySetBookingPageErrorState.importAndExecute(
                            BookingPageErrorState.ExpiredLink
                        );
                    } else if (bookableResponse?.bookingUrlType?.isSingleUse) {
                        lazySetBookingPageErrorState.importAndExecute(
                            BookingPageErrorState.SingleUseLinkAlreadyUsed
                        );
                    } else {
                        lazySetBookingPageErrorState.importAndExecute(
                            BookingPageErrorState.InvalidSignedUrl
                        );
                    }
                }
            }
        );
    } else {
        getBookingPageSessionData(bookingMailbox, meetingTypeId, bookingId, entryPoint);
    }
}

async function getBookingPageSessionData(
    bookingMailbox?: string,
    meetingTypeId?: string,
    bookingId?: string,
    entryPoint?: string,
    bookableResponse?: CheckBookableResponse | null
): Promise<boolean> {
    if (bookingMailbox) {
        getBookingSessionData(bookingMailbox, entryPoint, meetingTypeId, bookingId).then(
            (response: PersonalBookingData | null) => {
                if (response) {
                    if (isAnonymousUser()) {
                        const stringLanguage = getSupportedFallbackForLocale(
                            window.navigator.language
                        );
                        setLocale(stringLanguage.locale, stringLanguage.dir);
                        lazySetDisplayTimeZone.importAndExecute(getOSTimeZoneId());
                    } else {
                        lazySetDisplayTimeZone.importAndExecute(getUserWorkingHoursTzId());
                    }

                    // If there is a response from checkBookableAndUpdateTotalBookings then is a private meeting, proceed to check if is V1.
                    if (bookableResponse) {
                        getPrivateV1Meetingtype(meetingTypeId ?? '', bookableResponse, response);
                    }
                    lazySetBookingData.importAndExecute(response);
                    lazySetEditBooking.importAndExecute(!response.IsReadRequest);
                    // When the redirect after switching accounts is good, enable this:
                    // getAlternateAccountsFromIdentity();
                    lazySetInitialRequestState.importAndExecute(2);

                    // We handle this here because these can rely on data in the store.
                    if (!isAnonymousUser()) {
                        // Setup the cookie refresh if the user is logged in.
                        lazyInitiateCookieRefresh.importAndExecute();
                        lazyTrySetBookingUser.importAndExecute();
                    }
                    return true;
                }
                lazySetInitialRequestState.importAndExecute(3);
                return false;
            }
        );
    }
    return false;
}

// TODO: copied from FindTime voting page, should put it in a shared package.
function isAuthTokenExpired(authToken: string): boolean {
    const decodedToken = jwt_decode(authToken) as {
        exp: string;
    };
    const expiration = parseInt(decodedToken.exp) * 1000;
    const current: any = now();
    return expiration < current;
}

function getPrivateV1Meetingtype(
    meetingTypeId: string,
    bookableResponse: CheckBookableResponse,
    personalBookingData: PersonalBookingData
) {
    const meetingTypeDescription = parseMeetingTypeDescription(bookableResponse.description ?? '');

    if (bookableResponse.description && meetingTypeDescription.version) {
        logStartUsage('runAfterAttendeePageBootstrap', {
            meetingTypeId: bookableResponse.itemId,
            isPrivateV1: bookableResponse.isPrivate && meetingTypeDescription.version > 0,
            serviceEntryPoint: 'getPrivateV1Meetingtype',
        });
    }

    // personalBookingData (from GetBookingSessionData) will always get 1 BookingService, depending on the substrate flight OpenBookFilterPrivateMeetingTypes
    // that BookingService will be undefined when enabled the substrate filtering, or will be the service corresponding to meetingTypeId
    // (the encode booking service Id) when backend flight not enabled or is a legacy MeetingType with version == null.
    // bookableResponse, (response to checkBookableAndUpdateTotalBookings), has the all private MeetingType, when version ==1) properties,
    // to be used instead when ther private MeetingTpe is filtered from personalBookingData.
    // Note: bookableResponse.Id (guid) corresponds to meetingTypeId (encoded) but don't need to validate, the call to checkBookableAndUpdateTotalBookings
    // is already filterinhg for that Id.
    // DisplayedServiceNotFound is checked so that this personalBookingData was not found as a public meeting type, and services should be replaced with the private version.
    if (
        bookableResponse.description &&
        meetingTypeDescription.version &&
        meetingTypeDescription.version > 0 &&
        personalBookingData.Services.length == 0 &&
        personalBookingData.DisplayedServiceNotFound
    ) {
        const schedulingPolicyBookingService: SchedulingPolicy = {
            allowStaffSelection: bookableResponse.schedulingPolicy?.allowStaffSelection || false,
            capTimeInDays: bookableResponse.schedulingPolicy?.capTimeInDays || 90,
            hasCustomServiceWindows:
                bookableResponse.schedulingPolicy?.hasCustomServiceWindows || false,
            leadTimeForBookingsInMinutes:
                bookableResponse.schedulingPolicy?.leadTimeForBookingsInMinutes || 60,
            leadTimeForCancellationsInMinutes:
                bookableResponse.schedulingPolicy?.leadTimeForCancellationsInMinutes || 60,
            leadTimeInMinutes: bookableResponse.schedulingPolicy?.leadTimeInMinutes || 60,
            timeSlotIntervalInMinutes:
                bookableResponse.schedulingPolicy?.timeSlotIntervalInMinutes || 30,
        };

        const privateMeetingType: BookingService = {
            Id: meetingTypeId, // the same encoded 64 bookableResponse.Id
            Name: bookableResponse.displayName,
            Description: bookableResponse.description,
            AdditionalInformation: bookableResponse.additionalInformation,
            DurationMinutes: bookableResponse.durationMinutes || 30,
            FormattedDuration:
                bookableResponse.durationMinutes == null
                    ? '30 minutes'
                    : bookableResponse.durationMinutes.toString() + ' minutes',
            IsDefaultLocationOnline: bookableResponse.isDefaultLocationOnline || false,
            IsMultiCustomerBooking: false,
            SchedulingPolicy: schedulingPolicyBookingService,
            PreBuffer: bookableResponse.preBuffer ?? 0,
            PostBuffer: bookableResponse.postBuffer ?? 0,
        };
        //Injecting the private MT obtained form Checkbookable into the personablBookingdata list of "services" (Meetingtypes)
        personalBookingData.Services = [privateMeetingType];
    }
}

export function runAfterBootstrapOrganizer() {
    lazyRetrievePbEnabledStatus.importAndExecute();
}
